import { useState } from "react";
import SEO from "../components/SEO";
import EquivalenciesVisualizer from "../components/EquivalenciesVisualizer";
import { exportToPDF } from "../utils/pdfExport";
import {Dog, Bone, Trash2, Download, Share2} from 'lucide-react';

export default function PetCalculator() {
  const [dogSize, setDogSize] = useState("medium");
  const [foodType, setFoodType] = useState("dry");
  const [plasticWaste, setPlasticWaste] = useState("moderate");
  const [toysPurchased, setToysPurchased] = useState("");
  const [emissions, setEmissions] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<{food: number; waste: number; toys: number} | null>(null);

  const calculateEmissions = () => {
    // Dog food emissions based on size and type (tons CO2e per year)
    const foodFactors: Record<string, Record<string, number>> = {
      "small": { "dry": 0.3, "wet": 0.5, "raw": 0.8 },
      "medium": { "dry": 0.6, "wet": 1.0, "raw": 1.6 },
      "large": { "dry": 1.1, "wet": 1.8, "raw": 2.9 },
      "xlarge": { "dry": 1.8, "wet": 3.0, "raw": 4.8 },
    };
    const foodEmissions = foodFactors[dogSize][foodType];

    // Plastic waste (poop bags, packaging, etc.)
    const wasteFactors: Record<string, number> = {
      "minimal": 0.05,
      "moderate": 0.15,
      "high": 0.30,
    };
    const wasteEmissions = wasteFactors[plasticWaste];

    // Toys and accessories (0.5 kg CO2 per toy average)
    const toys = parseFloat(toysPurchased) || 0;
    const toyEmissions = (toys * 0.5) / 1000;

    const total = foodEmissions + wasteEmissions + toyEmissions;
    setEmissions(parseFloat(total.toFixed(2)));
    setBreakdown({
      food: parseFloat(foodEmissions.toFixed(2)),
      waste: parseFloat(wasteEmissions.toFixed(2)),
      toys: parseFloat(toyEmissions.toFixed(2)),
    });
  };

  const shareScore = () => {
    const text = `My dog's carbon pawprint is ${emissions} tons CO₂e/year! 🐾 Calculate yours at www.greenhousegascalculator.com/pet`;
    if (navigator.share) {
      navigator.share({ title: "Pet Carbon Pawprint", text });
    } else {
      navigator.clipboard.writeText(text);
      alert("Pawprint copied to clipboard! 🐾");
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Pet Carbon Pawprint Calculator",
    "url": "https://www.greenhousegascalculator.com/pet",
    "description": "Calculate your dog's carbon pawprint. Fun and educational tool to measure pet environmental impact from food, waste, and supplies.",
    "applicationCategory": "UtilityApplication",
  };

  return (
    <div className="bg-white">
      <SEO
        title="Carbon Pawprint Calculator | Dog Carbon Footprint Calculator"
        description="Free carbon pawprint calculator for dogs. Calculate your pet's environmental impact from food, plastic waste, and toys. Learn sustainable pet ownership tips."
        keywords="carbon pawprint calculator, dog carbon footprint, pet emissions calculator, sustainable pet ownership, eco-friendly pets"
        canonical="https://www.greenhousegascalculator.com/pet"
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-amber-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Dog className="w-16 h-16 text-[#2E8B57]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              🐾 Pet Carbon Pawprint Calculator
            </h1>
            <p className="text-xl text-gray-600">
              Discover your furry friend's environmental impact (it's not their fault!)
            </p>
          </div>
        </div>
      </section>

      {/* Educational Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Sustainable Pet Ownership: Reducing Your Dog's Carbon Pawprint
            </h2>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 mb-8">
              <p className="text-lg font-semibold text-gray-900">
                🐕 A medium-sized dog can have a carbon footprint of <span className="text-amber-600">up to 2 tons CO₂e per year</span> - similar to driving a car 3,700 miles!
              </p>
            </div>

            <p className="text-gray-700 mb-6">
              We love our pets, and they love us unconditionally. But did you know that pet ownership has an environmental impact? The good news: there are simple ways to reduce your dog's carbon pawprint without sacrificing their happiness!
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              🌱 6 Ways to Make Pet Ownership More Sustainable
            </h3>

            <div className="space-y-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">1. Choose Sustainable Dog Food</h4>
                <p className="text-gray-700">Insect-based or plant-based dog foods have 50% lower emissions than traditional meat-based options. Many dogs thrive on these alternatives!</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">2. Use Biodegradable Poop Bags</h4>
                <p className="text-gray-700">Switch from plastic to compostable bags. Over your dog's lifetime, this prevents thousands of plastic bags from landfills.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">3. Buy Durable, Eco-Friendly Toys</h4>
                <p className="text-gray-700">Choose toys made from recycled materials or natural rubber. They last longer and create less waste than cheap plastic toys.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">4. Adopt, Don't Shop</h4>
                <p className="text-gray-700">Rescuing a dog saves lives and reduces the environmental impact of breeding operations.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">5. DIY Treats & Toys</h4>
                <p className="text-gray-700">Make treats from kitchen scraps and toys from old t-shirts. Your dog won't know the difference, and you'll save money!</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">6. Walk, Don't Drive</h4>
                <p className="text-gray-700">Walk to the park instead of driving. It's better for your health, your dog's health, and the planet!</p>
              </div>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg mt-8">
              <p className="text-gray-700 font-medium">
                💙 <strong>Remember:</strong> Your dog's love is carbon-neutral! These tips help reduce environmental impact while keeping your pup happy and healthy.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Calculate Your Dog's Carbon Pawprint 🐾
            </h2>

            <div className="space-y-6 mb-8">
              <div className="bg-amber-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Dog className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Dog Size</label>
                </div>
                <select
                  value={dogSize}
                  onChange={(e) => setDogSize(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="small">Small (&lt;20 lbs) - Chihuahua, Pomeranian</option>
                  <option value="medium">Medium (20-50 lbs) - Beagle, Cocker Spaniel</option>
                  <option value="large">Large (50-90 lbs) - Labrador, German Shepherd</option>
                  <option value="xlarge">Extra Large (90+ lbs) - Great Dane, Mastiff</option>
                </select>
              </div>

              <div className="bg-amber-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Bone className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Type of Food</label>
                </div>
                <select
                  value={foodType}
                  onChange={(e) => setFoodType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="dry">Dry Kibble (Lowest Impact)</option>
                  <option value="wet">Wet/Canned Food</option>
                  <option value="raw">Raw/Fresh Meat Diet (Highest Impact)</option>
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  🌱 Tip: Insect-based or plant-based dog foods can reduce this by 50%!
                </p>
              </div>

              <div className="bg-amber-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Trash2 className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Plastic Waste (Bags, Packaging)</label>
                </div>
                <select
                  value={plasticWaste}
                  onChange={(e) => setPlasticWaste(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="minimal">Minimal (Biodegradable bags, bulk food)</option>
                  <option value="moderate">Moderate (Some plastic use)</option>
                  <option value="high">High (Regular plastic bags & packaging)</option>
                </select>
              </div>

              <div className="bg-amber-50 p-6 rounded-xl">
                <label className="font-semibold text-gray-900 block mb-4">
                  Toys & Accessories Purchased Per Year
                </label>
                <input
                  type="number"
                  value={toysPurchased}
                  onChange={(e) => setToysPurchased(e.target.value)}
                  placeholder="e.g., 12"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Include toys, collars, beds, bowls, etc.
                </p>
              </div>
            </div>

            <button
              onClick={calculateEmissions}
              className="w-full bg-[#2E8B57] text-white py-4 rounded-lg font-semibold hover:bg-[#267347] transition-colors"
            >
              Calculate Pawprint 🐾
            </button>

            {emissions !== null && breakdown && (
              <div className="mt-8 space-y-4">
                <div className="bg-gradient-to-r from-amber-50 to-green-50 p-6 rounded-xl border-2 border-[#2E8B57]">
                  <div className="text-center">
                    <p className="text-gray-600 mb-2">Your Dog's Annual Carbon Pawprint</p>
                    <p className="text-4xl font-bold text-[#2E8B57]">
                      {emissions} tons CO₂e
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {emissions < 1 ? "🌟 Excellent! Eco-friendly pup!" : 
                       emissions < 2 ? "✅ Good! Below average impact" :
                       "⚠️ Consider sustainable alternatives"}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-4">Pawprint Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">🍖 Food</span>
                      <span className="font-semibold">{breakdown.food} tons ({Math.round((breakdown.food/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">🗑️ Plastic Waste</span>
                      <span className="font-semibold">{breakdown.waste} tons ({Math.round((breakdown.waste/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">🎾 Toys & Accessories</span>
                      <span className="font-semibold">{breakdown.toys} tons ({Math.round((breakdown.toys/emissions)*100)}%)</span>
                    </div>
                  </div>
                </div>

                <EquivalenciesVisualizer emissions={emissions} />

                <div className="flex gap-4">
                  <button
                    onClick={() => exportToPDF({
                      title: "Pet Carbon Pawprint Report",
                      totalEmissions: emissions,
                      breakdown,
                      calculationType: "pet"
                    })}
                    className="flex-1 bg-[#2E8B57] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#267347] transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Export Report
                  </button>
                  <button
                    onClick={shareScore}
                    className="flex-1 bg-amber-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    Share Your Pawprint
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
